import { useState } from "react";
import {
  BanknotesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentListIcon,
  CurrencyPoundIcon,
  HashtagIcon,
  MapPinIcon,
  PhotoIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/outline";

import useListingStats from "../../../../data/useListingStats";
import CollapsibleSection from "../../../shared/CollapsibleSection";
import HeadlineStat from "../../../shared/HeadlineStat";
import LoadingWheel from "../../../shared/LoadingWheel";
import PriceFormat from "../../../../utils/priceFormat";
import UnlistedStatsTable from "./UnlistedStatsTable";

const moneyOrDash = (value: number | null | undefined): string =>
  value === null || value === undefined ? "—" : PriceFormat(value);

const countOrDash = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? "—"
    : value.toLocaleString("en-GB");

type UnlistedTableStatsProps = {
  readyToListActive?: boolean;
  onToggleReadyToList?: () => void;
};

export function UnlistedTableStats({
  readyToListActive,
  onToggleReadyToList,
}: UnlistedTableStatsProps = {}) {
  const { unlistedStats } = useListingStats();
  const [statsOpen, setStatsOpen] = useState(true);

  if (unlistedStats.isLoading) {
    return <LoadingWheel />;
  }

  const stats = unlistedStats.data?.data;
  if (!stats) {
    return null;
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setStatsOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50"
        aria-expanded={statsOpen}
      >
        <span>
          {statsOpen ? "Hide unlisted insights" : "Show unlisted insights"}
        </span>
        {statsOpen ? (
          <ChevronUpIcon className="h-4 w-4" />
        ) : (
          <ChevronDownIcon className="h-4 w-4" />
        )}
      </button>

      <CollapsibleSection isOpen={statsOpen}>
        <div className="mt-2 space-y-3">
          <div className="grid grid-cols-2 gap-2 px-1 sm:grid-cols-4">
            <HeadlineStat
              compact
              value={countOrDash(stats.partsReadyToList)}
              label="Ready to list"
              tone="emerald"
              icon={RocketLaunchIcon}
              sublabel={
                onToggleReadyToList
                  ? readyToListActive
                    ? "Filter on — click to clear"
                    : "Click to filter table"
                  : undefined
              }
              onClick={onToggleReadyToList}
              active={readyToListActive}
              title={
                onToggleReadyToList
                  ? "Show only parts that pass the listing workflow checks"
                  : undefined
              }
            />
            <HeadlineStat
              compact
              value={stats.awaitingListing.toLocaleString("en-GB")}
              label="Awaiting listing"
              tone="blue"
              icon={ClipboardDocumentListIcon}
            />
            <HeadlineStat
              compact
              value={stats.notLocated.toLocaleString("en-GB")}
              label="Not located"
              tone="rose"
              icon={MapPinIcon}
            />
            <HeadlineStat
              compact
              value={stats.missingPrice.toLocaleString("en-GB")}
              label="Missing price"
              tone="yellow"
              icon={CurrencyPoundIcon}
            />
            <HeadlineStat
              compact
              value={stats.missingMpn.toLocaleString("en-GB")}
              label="Missing MPN"
              tone="amber"
              icon={HashtagIcon}
            />
            <HeadlineStat
              compact
              value={stats.notImaged.toLocaleString("en-GB")}
              label="Not imaged"
              tone="violet"
              icon={PhotoIcon}
            />
            <HeadlineStat
              compact
              value={moneyOrDash(stats.unlistedValue)}
              label="Unlisted value"
              tone="emerald"
              icon={BanknotesIcon}
              sublabel="Of priced parts"
            />
          </div>

          <UnlistedStatsTable unlistedStats={stats} />
        </div>
      </CollapsibleSection>
    </div>
  );
}
